import { FC, ReactNode, useEffect } from "react";
import { twMerge } from "tailwind-merge";

import { useAv } from "../../../utils/av";
import { ClassChatSidebarPanel } from "../section/ClassChatSidebarPanel";
import { SectionUsersSidebarPanel } from "../section/SectionUsersSidebarPanel";
import { useIsAssessing } from "../section/assessment/hooks";
import { InstructorNotesSidebarPanel } from "../sidebar/InstructorNotesSidebarPanel";

import { AssessmentDisplayView } from "./assessment/AssessmentDisplayView";
import { AssessmentGradingView } from "./assessment/AssessmentGradingView";
import { PageOutcomeRubricSidebar } from "./outcomes/PageOutcomeRubricSidebar";

import { AIChatPanel } from "components/ai/components/AIChatPanel";
import {
  ASSESSMENT_VIEW_QUERY_PARAM,
  FOOTER_SECONDARY_OVERLAYS,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
  SecondaryOverlay,
  SecondaryOverlayType,
} from "components/constants.ts";
import { AssessmentSummary } from "components/materials/page/assessment/AssessmentSummary";
import { BreakoutsControlPanel } from "components/materials/sidebar/BreakoutsControlPanel";
import { shouldUseMobileRoutingOverlay } from "components/responsive-overlay-routing";
import { t } from "i18n/i18n";
import { useIsLgOrLarger, useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  pageSelectors,
  selectAssessmentBySubmissionId,
  selectIsRecordingAgent,
  selectIsUnassessablePage,
} from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { useQueryParam, useQueryParamsActions } from "utils/routing";

type OverlayComponentProps = {
  overlay: SecondaryOverlayType;
  pageId?: string | null;
};

const OverlayComponent: FC<OverlayComponentProps> = ({ overlay, pageId }) => {
  switch (overlay) {
    case SecondaryOverlay.Outcomes:
      return pageId ? <PageOutcomeRubricSidebar pageId={pageId} /> : null;

    case SecondaryOverlay.AssessmentSummary:
      return pageId ? <AssessmentSummary pageId={pageId} /> : null;

    case SecondaryOverlay.BreakoutSettings:
      return <BreakoutsControlPanel />;

    case SecondaryOverlay.AIAssistant:
      return <AIChatPanel />;

    case SecondaryOverlay.Users:
      return <SectionUsersSidebarPanel />;

    case SecondaryOverlay.InstructorNotes:
      return <InstructorNotesSidebarPanel />;

    case SecondaryOverlay.ClassChat:
      return <ClassChatSidebarPanel />;

    default:
      return null;
  }
};

const OverlaySidebar: FC<{
  className?: string;
  title?: string;
  children: ReactNode | ReactNode[];
}> = ({ className, children, title }) => (
  <aside aria-label={title || t("glossary.secondary_sidebar")} className={className}>
    {children}
  </aside>
);

type PageSecondarySidebarProps = {
  className?: string;
  shouldShowOverlay?: boolean;
  pageId?: string | null;
};

export const PageSecondarySidebar: FC<PageSecondarySidebarProps> = ({
  className,
  pageId,
  shouldShowOverlay,
}) => {
  const { deleteQueryParams } = useQueryParamsActions();

  const { canAuthorSection } = useCapabilities();
  const isAssessing = useIsAssessing();
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["assessments_published_at"],
  );
  const assessmentPublishedAt = page?.assessments_published_at ?? null;
  const isOnUnassessablePage = useAppSelector((s) => selectIsUnassessablePage(s, pageId));
  const { isInVirtualClassOrObserving } = useAv();
  const isMdOrLarger = useIsMdOrLarger();
  const isLgOrLarger = useIsLgOrLarger();

  const selectedOverlayRoute = useQueryParam(
    SECONDARY_OVERLAY_QUERY_PARAM,
  ) as SecondaryOverlayType | null;
  const submissionId = useQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  // The recording agent opens the section at desktop size without selecting a panel.
  // It still needs Chat in the recording, so render Chat in this sidebar.
  const overlayToRender = isRecordingAgent ? SecondaryOverlay.ClassChat : selectedOverlayRoute;
  const assessment = useObjectSelector(
    (s) => selectAssessmentBySubmissionId(s, submissionId),
    ["id"],
  );
  const showingFooterSecondaryOverlay =
    selectedOverlayRoute != null &&
    (FOOTER_SECONDARY_OVERLAYS as string[]).includes(selectedOverlayRoute);

  useEffect(() => {
    if (isAssessing && isOnUnassessablePage) {
      deleteQueryParams([ASSESSMENT_VIEW_QUERY_PARAM]);
    }
  }, [deleteQueryParams, isAssessing, isOnUnassessablePage]);

  // Shared panels have one host: MobileRoutingOverlay below lg and this sidebar at lg+.
  if (shouldUseMobileRoutingOverlay(selectedOverlayRoute, isLgOrLarger)) return null;

  // Need to add an overlay backdrop on the page secondary sidebar area if the sidebar
  // isn't accessible via the footer.
  const overlayBackdrop = shouldShowOverlay ? (
    <div className="absolute inset-0 z-10 bg-black-tint-95/80 backdrop-blur-sm" />
  ) : null;

  if (overlayToRender) {
    return (
      <OverlaySidebar
        className={twMerge(
          className,
          // Since this also includes secondary sidebars not accessible via
          // the footer, we want to only blur the background (and not the
          // content) if there is a footer-accessible secondary sidebar.
          shouldShowOverlay &&
            showingFooterSecondaryOverlay &&
            "bg-black-tint-95/80 backdrop-blur-sm",
        )}
      >
        <OverlayComponent overlay={overlayToRender} pageId={pageId} />
        {/* Otherwise, we will blur the entire secondary sidebar. */}
        {!showingFooterSecondaryOverlay && overlayBackdrop}
      </OverlaySidebar>
    );
  }

  if (isAssessing && pageId && !isOnUnassessablePage) {
    return (
      <OverlaySidebar
        className={twMerge(className, isMdOrLarger && !isInVirtualClassOrObserving && "px-0 pr-6")}
        title={t("assessment.assessment_panel")}
      >
        <AssessmentGradingView pageId={pageId} />
        {overlayBackdrop}
      </OverlaySidebar>
    );
  }

  const isViewingAssessment = assessment && pageId && (canAuthorSection || assessmentPublishedAt);
  if (isViewingAssessment) {
    return (
      <OverlaySidebar className={className}>
        <AssessmentDisplayView pageId={pageId} />
        {overlayBackdrop}
      </OverlaySidebar>
    );
  }

  return null;
};
